import {Form, FormField, FormSubmitHandler, Label} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {isFalse} from 'ramda-adjunct';

import {useGetProformaGenerationQuery, usePutProformaGenerationMutation} from '@dms/api/finance';
import {PutProformaGenerationApiArg} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, NullishBoolean, useRequiredParams, useNavigate} from 'shared/web';

const getProformaGenerationSchema = (isSystem: NullishBoolean) => {
  const branchIdSchema = isFalse(isSystem) ? z.string().nonempty() : z.string().nullable();

  return z.object({
    branchId: branchIdSchema,
    unify: z.boolean(),
  });
};

type FormValues = z.infer<ReturnType<typeof getProformaGenerationSchema>>;

export function ProformaInvoiceGenerationDetail() {
  const {id} = useRequiredParams();
  const navigate = useNavigate();
  const [updateProformaGeneration] = usePutProformaGenerationMutation();

  const {
    data: proformaGeneration,
    isLoading: isProformaGenerationLoading,
    isError: isProformaGenerationError,
  } = useGetProformaGenerationQuery({id});
  const {
    data: branches,
    branchOptions,
    isLoading: isBranchesLoading,
    isError: isBranchesError,
  } = useBranches();

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await updateProformaGeneration({...data, id} as PutProformaGenerationApiArg)
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.proformaGeneration);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const isLoading = isProformaGenerationLoading || isBranchesLoading;
  const isError = isProformaGenerationError || isBranchesError;

  const branchName =
    branches?.branchListItems.find((branch) => branch.id === proformaGeneration?.branchId)
      ?.marketingName ?? i18n.t('page.accounting.labels.tenantDefinition');

  const defaultValues = {
    unify: proformaGeneration?.unify ?? false,
    branchId: proformaGeneration?.branchId,
  };

  return (
    <SettingsTemplate
      header={{
        title: branchName,
      }}
      data-testid={testIds.settings.proformaGeneration('page')}
      isLoading={isLoading}
      isError={isError}
    >
      <Form<FormValues>
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        experimentalZodSchema={getProformaGenerationSchema(proformaGeneration?.isSystem)}
        shouldWatchForUnsavedChanges
      >
        {(control, _formApi, navigateWithoutBlocker) => (
          <VStack spacing={4}>
            <SettingsSection>
              <Grid columns={2}>
                <FormField
                  control={control}
                  type="choice"
                  name="branchId"
                  isNotClearable
                  options={branchOptions}
                  isDisabled={proformaGeneration?.isSystem}
                  label={i18n.t('entity.cashRegister.parameters.branch')}
                  data-testid={testIds.settings.proformaGeneration('branch')}
                  placeholder={
                    proformaGeneration?.isSystem
                      ? i18n.t('page.accounting.labels.tenantDefinition')
                      : undefined
                  }
                  isRequired
                />
              </Grid>
            </SettingsSection>
            <VStack spacing={1}>
              <Label>{i18n.t('page.accountingSettings.labels.generationType')}</Label>
              <FormField
                control={control}
                type="checkbox"
                name="unify"
                data-testid={testIds.settings.proformaGeneration('unify')}
                label={i18n.t('page.accountingSettings.labels.unify')}
              />
            </VStack>
            <SettingsFooter
              actions={[
                {
                  type: 'button',
                  title: i18n.t('general.actions.discardChanges'),
                  onClick: () => navigateWithoutBlocker(settingsRoutes.proformaGeneration),
                  variant: 'secondary',
                  'data-testid': testIds.settings.proformaGeneration('discard'),
                },
                {
                  type: 'form-button',
                  control,
                  buttonType: 'submit',
                  title: i18n.t('general.actions.saveChanges'),
                  'data-testid': testIds.settings.proformaGeneration('save'),
                },
              ]}
            />
          </VStack>
        )}
      </Form>
    </SettingsTemplate>
  );
}
